import { useEffect, useRef, useState } from 'react';
import { Eyebrow } from '@/components/ui';
import { Check } from 'lucide-react';
import { BraindayError } from '@/services/BraindayAPI';
import type { AiAction } from '@/types';
import type { ChatTurn } from './useCommand';
import type { ActionOutcomeState } from '@/stores/askThreadStore';

const SUGGESTIONS = ['Plan my afternoon', 'What should I do next?', 'Move my 3 PM', 'Add a task'];

export function errorMessage(error: unknown): string {
  if (!(error instanceof BraindayError)) return 'Something went wrong. Try again?';
  switch (error.code) {
    case 'offline':
      return "You're offline. Try again when you're back.";
    case 'rate-limit':
      return 'Daily AI limit reached. Resets at midnight UTC.';
    case 'timeout':
      return 'That took too long. Try again?';
    case 'app-check':
      return 'App verification failed. Reload the app and try again.';
    case 'server':
      return 'AI service had a problem. Try again in a moment.';
    default:
      return 'Something went wrong. Try again?';
  }
}

interface ActionOutcome {
  state: ActionOutcomeState;
  /** Present only for an action applied in THIS session — powers the inline Undo.
   * Outcomes restored from persistence (after reopen) have no undo. */
  undo?: (() => void) | undefined;
}

export interface AskThreadProps {
  messages: ChatTurn[];
  streamingText: string;
  loading: boolean;
  error: BraindayError | null;
  /** Persisted per-action outcomes (keyed "i:j"); seeds the rendered state so a
   * settled card stays settled after the sheet is closed and reopened. */
  initialOutcomes: Record<string, ActionOutcomeState>;
  onAction: (action: AiAction) => (() => void) | void;
  onMarkOutcome: (key: string, state: ActionOutcomeState) => void;
  onClearOutcome: (key: string) => void;
  onSuggestion: (text: string) => void;
}

export function AskThread({
  messages,
  streamingText,
  loading,
  error,
  initialOutcomes,
  onAction,
  onMarkOutcome,
  onClearOutcome,
  onSuggestion,
}: AskThreadProps) {
  // First message's content is a cheap conversation identity: when the thread
  // resets to [] the identity becomes '' and prior outcomes are ignored.
  const conversationId = messages[0]?.content ?? '';
  const [acted, setActed] = useState<{ id: string; map: Record<string, ActionOutcome> }>(() => ({
    id: conversationId,
    map: Object.fromEntries(Object.entries(initialOutcomes).map(([k, s]) => [k, { state: s }])),
  }));
  const activeActed = acted.id === conversationId ? acted.map : {};

  const setOutcome = (key: string, outcome: ActionOutcome | null) =>
    setActed((p) => {
      const base = p.id === conversationId ? { ...p.map } : {};
      if (outcome) base[key] = outcome;
      else delete base[key];
      return { id: conversationId, map: base };
    });

  const isEmpty = messages.length === 0 && !loading && !error;

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, streamingText, loading]);

  return (
    // aria-busy suppresses live-region announcements while tokens stream in —
    // without it, SRs re-read the mutating text (and the ▍ cursor) per chunk.
    <div className="space-y-3" aria-live="polite" aria-busy={loading || undefined}>
      {isEmpty ? (
        <div className="flex flex-wrap gap-2 py-2" role="group" aria-label="Suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSuggestion(s)}
              className="pressable rounded-full border border-line-2 bg-card px-3 py-1.5 text-body text-t2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas pointer-coarse:min-h-[44px]"
            >
              {s}
            </button>
          ))}
        </div>
      ) : (
        <>
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'text-t2' : 'text-t1'}>
              <Eyebrow className="font-medium">{m.role === 'user' ? 'You' : 'Brainday'}</Eyebrow>
              <div className="mt-0.5 whitespace-pre-wrap break-words text-body-lg">{m.content}</div>
              {m.role === 'assistant' && m.actions && m.actions.length > 0 ? (
                <div className="mt-2 flex flex-col gap-2">
                  {m.actions.map((a, j) => {
                    const key = `${i}:${j}`;
                    const outcome = activeActed[key];
                    if (outcome?.state === 'cancelled') return null;

                    // Applied: persistent inline confirmation (with optional Undo),
                    // not the Do it / Cancel buttons — so it's clear it stuck.
                    if (outcome?.state === 'done') {
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2 rounded-lg border border-ok-ink bg-ok-bg px-3 py-2 text-body text-t1"
                        >
                          <Check className="h-3.5 w-3.5 shrink-0 text-ok" aria-hidden />
                          <span className="flex-1">{a.label}</span>
                          {outcome.undo ? (
                            <button
                              type="button"
                              onClick={() => {
                                outcome.undo?.();
                                setOutcome(key, null);
                                onClearOutcome(key);
                              }}
                              className="rounded-md px-2 py-1 text-t3 can-hover:hover:text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                            >
                              Undo
                            </button>
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 rounded-lg border border-line-2 bg-card px-3 py-2 text-body"
                      >
                        <span className="flex-1">{a.label}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const undo = onAction(a);
                            setOutcome(key, {
                              state: 'done',
                              undo: typeof undo === 'function' ? undo : undefined,
                            });
                            onMarkOutcome(key, 'done');
                          }}
                          className="rounded-md bg-t1 px-2 py-1 text-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                        >
                          Do it
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOutcome(key, { state: 'cancelled' });
                            onMarkOutcome(key, 'cancelled');
                          }}
                          className="rounded-md border border-line-2 px-2 py-1 text-t2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                        >
                          Cancel
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
          {loading ? (
            <div className="text-t1">
              <Eyebrow className="font-medium">Brainday</Eyebrow>
              <div className="mt-0.5 whitespace-pre-wrap break-words text-body-lg">
                {streamingText}
                <span className="ml-1 inline-block animate-pulse motion-reduce:animate-none">
                  ▍
                </span>
              </div>
            </div>
          ) : null}
          {error ? (
            <div className="rounded-lg border border-warn-ink bg-warn-bg p-3 text-body text-t1">
              {errorMessage(error)}
            </div>
          ) : null}
          <div ref={bottomRef} aria-hidden />
        </>
      )}
    </div>
  );
}
